import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  type NormalizedCacheObject,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { persistCache } from 'apollo3-cache-persist';
import { SelectiveCachePersistence } from './cache/selective-cache-persistence';
import {
  CACHE_PERSISTENCE_CONFIG,
  DEV_CACHE_PERSISTENCE_CONFIG,
} from './cache/cache-config';
import { ALLOWED_OPERATIONS } from './constants';

export async function getApolloClient(
  token: string,
): Promise<ApolloClient<NormalizedCacheObject>> {
  const authMiddleware = new ApolloLink((operation, forward) => {
    if (token) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
    } else if (
      !operation.operationName ||
      !ALLOWED_OPERATIONS.includes(
        operation.operationName as (typeof ALLOWED_OPERATIONS)[number],
      )
    ) {
      throw new Error('No access token available.');
    }

    return forward(operation);
  });

  // Create selective cache persistence instance with environment-based configuration
  const isProduction = import.meta.env.PROD;
  const cacheConfig = isProduction
    ? CACHE_PERSISTENCE_CONFIG
    : DEV_CACHE_PERSISTENCE_CONFIG;
  const cachePersistence = new SelectiveCachePersistence(cacheConfig);

  const cache = new InMemoryCache();

  // Create a custom storage wrapper that filters data
  const selectiveStorage = {
    getItem: async (key: string): Promise<string | null> => {
      const data = window.localStorage.getItem(key);
      if (!data) return null;

      try {
        const persistentData = JSON.parse(data);
        const restoredCache =
          cachePersistence.restoreCacheFromPersistence(persistentData);

        return JSON.stringify(restoredCache);
      } catch (_error) {
        return data; // Fallback to original data
      }
    },

    setItem: async (key: string, value: string): Promise<void> => {
      try {
        const cacheData = JSON.parse(value);
        const persistentData =
          cachePersistence.filterCacheForPersistence(cacheData);
        const prunedData = cachePersistence.pruneBySize(persistentData);

        window.localStorage.setItem(key, JSON.stringify(prunedData));
      } catch (_error) {
        window.localStorage.setItem(key, value); // Fallback to original data
      }
    },

    removeItem: async (key: string): Promise<void> => {
      window.localStorage.removeItem(key);
    },
  };

  // Set up cache persistence with custom storage
  await persistCache({
    cache,
    storage: selectiveStorage,
    key: `apollo-cache-persist-${token || 'guest'}`,
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        // eslint-disable-next-line no-console
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`);
    }
  });

  const httpLink = createUploadLink({
    uri: `${import.meta.env?.VITE_GRAPHQL_URL || '/'}api/graphql`,
    headers: {
      'keep-alive': 'true',
    },
  });

  return new ApolloClient({
    link: from([errorLink, authMiddleware, httpLink as unknown as ApolloLink]),
    cache,
    credentials: 'same-origin',
    connectToDevTools: !import.meta.env.PROD,
  });
}
